import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Drawer, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';
import type { ICampaign, ICampaignAsset } from '../../../redux/buzzbridge/types';
import { updateOffsiteCampaign } from '../../../redux/buzzbridge/actions';
import { updatingCampaignSelector } from '../../../redux/buzzbridge/selectors';
import CampaignAssetsEditor, { type AssetEntry } from './CampaignAssetsEditor';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_SURFACE_CONTAINER_LOW,
    BRAND_SURFACE_CONTAINER,
} from '../brandDashboardTokens';
import { FONT_ENUM } from '../../../types/global/fonts.types';

function FieldLabel({ children }: { children: React.ReactNode }) {
    return (
        <Typography
            sx={{
                fontFamily: FONT_ENUM.MAIN_BOLD,
                fontSize: 11,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: '#fff',
                mb: 1.5,
            }}>
            {children}
        </Typography>
    );
}

interface Props {
    open: boolean;
    onClose: () => void;
    campaign: ICampaign;
}

export function CampaignAssetsPanel({ open, onClose, campaign }: Props) {
    const dispatch = useDispatch();
    const updating = useSelector(updatingCampaignSelector);

    const [assets, setAssets] = useState<AssetEntry[]>([]);

    useEffect(() => {
        if (!open) return;
        setAssets(
            (campaign.assets || []).map((a: ICampaignAsset) => ({
                url: a.url,
                description: a.description || '',
                requirements: a.requirements || [],
            })),
        );
    }, [open, campaign]);

    const handleSave = () => {
        const hasNewFiles = assets.some(a => a.file);
        const currentAssets = assets.map(a => ({
            url: a.url,
            file: a.file,
            description: a.description || undefined,
            requirements: a.requirements.length > 0 ? a.requirements : undefined,
        }));
        const origAssets = (campaign.assets || []).map((a: ICampaignAsset) => ({
            url: a.url,
            description: a.description || undefined,
            requirements: a.requirements && a.requirements.length > 0 ? a.requirements : undefined,
        }));
        const changed =
            hasNewFiles ||
            JSON.stringify(currentAssets.map(({ file, ...rest }) => rest)) !== JSON.stringify(origAssets);

        if (!changed) { onClose(); return; }
        dispatch(updateOffsiteCampaign({ campaignId: campaign._id, body: { rawAssets: currentAssets } }));
        onClose();
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: { xs: '100vw', sm: 520 },
                    bgcolor: '#111',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 3,
                    py: 2.5,
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                }}>
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 16, color: '#fff' }}>
                    Campaign Assets
                </Typography>
                <IconButton size="small" onClick={onClose} sx={{ color: 'rgba(255,255,255,0.5)' }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            {/* Body */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3 }}>
                <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 3, borderRadius: 2 }}>
                    <FieldLabel>Campaign Assets</FieldLabel>
                    <Typography
                        sx={{
                            fontSize: 13,
                            color: 'rgba(255,255,255,0.55)',
                            mb: 2,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            lineHeight: 1.55,
                        }}>
                        Zip all files into one archive and upload here. Only .zip files are supported.
                    </Typography>
                    <CampaignAssetsEditor assets={assets} onChange={setAssets} />
                </Box>
            </Box>

            {/* Footer */}
            <Box
                sx={{
                    px: 3,
                    py: 2.5,
                    borderTop: '1px solid rgba(255,255,255,0.06)',
                    bgcolor: `${BRAND_SURFACE_CONTAINER}80`,
                    display: 'flex',
                    gap: 2,
                    flexDirection: 'row-reverse',
                }}>
                <Button
                    onClick={handleSave}
                    disabled={updating}
                    sx={{
                        px: 4,
                        py: 1.5,
                        bgcolor: BRAND_LUXE_PRIMARY,
                        color: '#000',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        borderRadius: 3,
                        '&:hover': { bgcolor: '#e6b500' },
                        '&.Mui-disabled': { bgcolor: 'rgba(255,201,101,0.3)', color: 'rgba(0,0,0,0.4)' },
                        minWidth: 100,
                    }}>
                    {updating ? <CircularProgress size={16} sx={{ color: '#000' }} /> : 'Save'}
                </Button>
                <Button
                    onClick={onClose}
                    sx={{
                        px: 3,
                        py: 1.5,
                        color: 'rgba(255,255,255,0.6)',
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        borderRadius: 3,
                        border: '1px solid rgba(255,255,255,0.1)',
                    }}>
                    Cancel
                </Button>
            </Box>
        </Drawer>
    );
}
